import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

export function ConnectionsPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const connections = useQuery({
    queryKey: ['connections'],
    queryFn: api.connections,
  });

  // The OAuth callback redirects back here with a status in the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      const declined = params.get('declined');
      setNotice({
        kind: declined ? 'error' : 'ok',
        text: declined
          ? t('connections.connectedDeclined', { list: declined })
          : t('connections.connected', { count: params.get('pages') ?? 0 }),
      });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error')) {
      setNotice({
        kind: 'error',
        text: params.get('detail') ?? params.get('error') ?? t('common.requestFailed'),
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t]);

  const connect = useMutation({
    mutationFn: api.startOAuth,
    onSuccess: (data) => {
      // Full navigation, not a popup: Meta blocks the dialog in many embedded
      // contexts, and a top-level redirect is what the manual flow expects.
      window.location.href = data.authorizationUrl;
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  const verify = useMutation({
    mutationFn: api.verifyConnection,
    onSuccess: (result) => {
      setNotice({
        kind: result.valid ? 'ok' : 'error',
        text: result.valid
          ? result.expiresAt
            ? t('connections.tokenValidUntil', {
                date: fmt.dateTime(result.expiresAt),
              })
            : t('connections.tokenValid')
          : (result.reason ?? t('common.requestFailed')),
      });
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });

  const disconnect = useMutation({
    mutationFn: api.disconnect,
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('connections.disconnected') });
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  if (connections.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (connections.isError) return <div className="notice error">{t('common.requestFailed')}</div>;

  const data = connections.data;
  const allowance = data?.metaAccounts ?? { used: 0, limit: null };
  // `null` is a plan with no ceiling, which is not the same as a ceiling of nothing.
  const atLimit = allowance.limit !== null && allowance.used >= allowance.limit;

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('connections.title')}</h2>
        {/* "Connect a Meta account" beside an account that is already
            connected reads as "reconnect this one". Once there is one, the
            button says what it actually does: add another. */}
        <button
          type="button"
          className="primary"
          onClick={() => connect.mutate()}
          disabled={connect.isPending || atLimit}
        >
          <Icon.plus />
          {(data?.connections.length ?? 0) > 0
            ? t('connections.connectAnother')
            : t('connections.connect')}
        </button>
      </div>

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {/* Said before somebody is refused rather than after. More than one Meta
          account is normal here — an agency's own and a client's, or a business
          split across two logins. */}
      <p className="tiny muted">
        {atLimit
          ? t('connections.atLimit', { limit: String(allowance.limit ?? 0) })
          : t('connections.allowance', {
              used: String(allowance.used),
              limit: allowance.limit === null ? '∞' : String(allowance.limit),
            })}
      </p>

      <div className="meta-facts">
        <span>
          {t('connections.graphVersion')} <strong className="ltr">{data?.graphVersion}</strong>
        </span>
        <span>
          {t('connections.publicSource')}:{' '}
          <strong>
            {data?.publicPageSourceEnabled ? t('connections.enabled') : t('connections.disabled')}
          </strong>
        </span>
        <span>
          {t('connections.requestedScopes')}:{' '}
          <span className="mono tiny">{data?.requestedScopes.join(', ')}</span>
        </span>
      </div>

      {!data?.publicPageSourceEnabled && (
        <div className="notice">{t('connections.publicSourceNotice')}</div>
      )}

      {data?.connections.length === 0 && (
        <div className="empty-state">
          <Icon.link />
          <p>{t('connections.empty')}</p>
        </div>
      )}

      {data?.connections.map((connection) => (
        <article key={connection.id} className="card">
          <header className="card-head">
            <h3>{connection.metaUserName}</h3>
            <span className={`badge ${connection.status === 'ACTIVE' ? 'ok' : 'warn'}`}>
              {connection.status}
            </span>
          </header>

          <dl className="facts">
            <dt>{t('connections.pages')}</dt>
            <dd>{connection.pageCount}</dd>
            <dt>{t('connections.tokenStored')}</dt>
            <dd>{connection.tokenPresent ? t('connections.tokenEncrypted') : t('common.no')}</dd>
            <dt>{t('connections.tokenExpires')}</dt>
            <dd>
              {connection.tokenExpiresAt
                ? fmt.dateTime(connection.tokenExpiresAt)
                : t('connections.noExpiry')}
            </dd>
            <dt>{t('connections.lastVerified')}</dt>
            <dd>
              {connection.lastVerifiedAt
                ? fmt.dateTime(connection.lastVerifiedAt)
                : t('common.never')}
            </dd>
          </dl>

          {connection.declinedScopes.length > 0 && (
            <p className="error small">
              {t('connections.declined')}: {connection.declinedScopes.join(', ')}
            </p>
          )}
          {connection.missingScopes.length > 0 && (
            <p className="warn small">
              {t('connections.notGranted')}: {connection.missingScopes.join(', ')}
            </p>
          )}
          {connection.pendingScopes.length > 0 && (
            <p className="muted small">
              {t('connections.pendingApproval')}: {connection.pendingScopes.join(', ')}
            </p>
          )}
          {connection.invalidationReason && (
            <p className="error small">{connection.invalidationReason}</p>
          )}

          <div className="actions">
            <button
              type="button"
              onClick={() => verify.mutate(connection.id)}
              disabled={verify.isPending}
            >
              <Icon.check />
              {t('connections.verify')}
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => {
                if (window.confirm(t('connections.confirmDisconnect'))) {
                  disconnect.mutate(connection.id);
                }
              }}
              disabled={disconnect.isPending}
            >
              {t('connections.disconnect')}
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}
